import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  WHOLE_GROUP_FILTER,
  fetchNewsContext,
  fetchPosts,
  createPost,
  getPostById,
  pinPost,
  acknowledgePost,
  setEventResponse,
  toggleReaction,
  addComment,
  deleteComment,
  updateComment,
  updatePost,
  deletePost,
  getFileUrl,
} from "services/posts.service";
import { upload, isEmptyUploadError } from "services/upload-file.service";
import {
  EventResponse,
  NewsContext,
  PinRequest,
  Post,
  PostComment,
  PostPin,
  Reaction,
  ReactionSummary,
} from "types/Post";
import { PostForm, PostFormSubmission } from "./components/PostForm";
import { PostCard, postElementId } from "./components/PostCard";
import { PostSkeletonList } from "./components/PostSkeleton";
import { FeedFilter } from "./components/FeedFilter";
import { ComposerPill } from "./components/ComposerPill";
import { NewsHeader } from "./components/NewsHeader";
import { NewsSidebar } from "./components/NewsSidebar";
import { useNewsSidebar } from "./hooks/useNewsSidebar";
import { toggleReactionOptimistically } from "./utils/reactions";

type PinnedPost = Post & { pin: PostPin };
const isPinned = (post: Post): post is PinnedPost => post.pin != null;

export default function Posts() {
  const { companyId } = useParams<{ companyId: string }>();

  if (!companyId) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="text-center text-gray-500">Company ID is required</div>
      </div>
    );
  }

  // One feed per company page: switching company starts with a clean feed, filter and composer.
  return <CompanyFeed key={companyId} companyId={companyId} />;
}

function CompanyFeed({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Kept apart from `error`: loading posts clears `error`, but without the context
  // the composer and filter stay hidden, so this message stays until the context loads.
  const [contextError, setContextError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  // The composer starts as a one-line prompt and opens into the full form,
  // with the event fields on when the organiser came from «Sukurti renginį» in the sidebar.
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerStartsAsEvent, setComposerStartsAsEvent] = useState(false);
  // What the viewer may do here and which group the company is in (null = not in a group).
  const [newsContext, setNewsContext] = useState<NewsContext | null>(null);
  // WHOLE_GROUP_FILTER, or the id of the one group company whose posts are shown.
  const [filter, setFilter] = useState<string>(WHOLE_GROUP_FILTER);
  const { sidebar, reloadSidebar } = useNewsSidebar(companyId, filter);
  // The post of the event the viewer picked in the sidebar, until the feed has scrolled to it.
  const [postIdToScrollTo, setPostIdToScrollTo] = useState<string | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  // Every first-page request starts a new generation. An answer of an older one
  // (another filter, a refresh) is dropped so it never mixes into the current feed.
  const feedGeneration = useRef(0);
  // Same guard for the context request: only the answer of the latest request is used.
  const contextGeneration = useRef(0);

  const viewer = newsContext?.viewer;
  const group = newsContext?.group ?? null;

  // Fetch posts
  const loadPosts = useCallback(
    async (page: number, append: boolean = false) => {
      if (!append) feedGeneration.current += 1;
      const generation = feedGeneration.current;
      const isStale = () => generation !== feedGeneration.current;

      try {
        if (page === 1) {
          setIsLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const response = await fetchPosts(companyId, page, filter);

        if (isStale()) return;

        if (append) {
          // A post opened from the sidebar may already be on the page.
          setPosts((prev) => [
            ...prev,
            ...response.postsCollection.filter((post) => !prev.some((shown) => shown.id === post.id)),
          ]);
        } else {
          setPosts([...response.pinnedPosts, ...response.postsCollection]);
        }

        setHasNextPage(response.pagination.hasNextPage);
        setCurrentPage(page);
      } catch (err: any) {
        if (isStale()) return;
        setError(t("feed_failed_to_load_posts"));
      } finally {
        if (!isStale()) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [companyId, filter, t]
  );

  const loadNewsContext = useCallback(() => {
    contextGeneration.current += 1;
    const generation = contextGeneration.current;
    fetchNewsContext(companyId)
      .then((context) => {
        if (generation !== contextGeneration.current) return;
        setNewsContext(context);
        setContextError(null);
      })
      .catch((err) => {
        if (generation !== contextGeneration.current) return;
        console.error("Error loading news context:", err);
        setContextError(t("feed_failed_to_load_news_context"));
      });
  }, [companyId, t]);

  useEffect(() => {
    loadNewsContext();
  }, [loadNewsContext]);

  // First page, on mount and whenever the filter (and so loadPosts) changes
  useEffect(() => {
    loadPosts(1, false);
  }, [loadPosts]);

  // The posts, page and "has more" of the old filter all go at once; loadPosts then fetches the new first page.
  const handleFilterChange = useCallback((nextFilter: string) => {
    setFilter(nextFilter);
    setPosts([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setIsLoading(true);
    setEditingPost(null);
    // An event picked in the sidebar for the old filter may not belong to the new one.
    setPostIdToScrollTo(null);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          loadPosts(currentPage + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isLoadingMore, isLoading, currentPage, loadPosts]);


  // While a post is being edited the edit form holds the composer's place;
  // opening the composer then would silently drop the unsaved edit.
  const openComposer = useCallback(
    (asEvent: boolean) => {
      if (editingPost) return;
      setComposerStartsAsEvent(asEvent);
      setIsComposerOpen(true);
    },
    [editingPost]
  );

  // Handle post creation
  const handleCreatePost = useCallback(
    async ({ content, details, isImportant, files, isGroupPost, event }: PostFormSubmission) => {
      if (!companyId) return;

      setIsSubmitting(true);
      try {
        // Upload files first if any
        let fileIds: string[] = [];
        if (files.length > 0) {
          try {
            // Upload files and extract file IDs
            // Based on other components, upload() returns axios response with data being the file ID or file entity
            const uploadFailures: unknown[] = [];
            for (const file of files) {
              try {
                const response = await upload(file, companyId);
                const fileData = response.data;
                
                // Extract file ID - could be string ID directly, or file entity with id field
                let fileId: string;
                if (typeof fileData === 'string') {
                  fileId = fileData;
                } else if (fileData?.id) {
                  fileId = fileData.id;
                } else if (fileData?.fileId) {
                  fileId = fileData.fileId;
                } else {
                  throw new Error("Failed to extract file ID from upload response");
                }
                
                fileIds.push(fileId);
              } catch (fileError) {
                uploadFailures.push(fileError);
              }
            }
            
            if (fileIds.length === 0) {
              // Empty files were already toasted by upload(); when they are
              // the only failures, rethrow one so the catches below stay quiet.
              throw uploadFailures.every(isEmptyUploadError)
                ? uploadFailures[0]
                : new Error("Failed to upload any files");
            }
          } catch (fileError) {
            console.error("Error uploading files:", fileError);
            if (!isEmptyUploadError(fileError)) setError(t("feed_failed_to_upload_files"));
            setIsSubmitting(false);
            throw fileError;
          }
        }

        // Create post with fileIds (filter out any invalid IDs)
        const validFileIds = fileIds.filter((id) => id && typeof id === 'string');
        const newPost = await createPost({
          companyId,
          content,
          details,
          isImportant,
          fileIds: validFileIds.length > 0 ? validFileIds : undefined,
          isGroupPost,
          event,
        });
        setIsComposerOpen(false);

        // The new post belongs to this company. If the feed shows another company, go back to the whole group so the post is visible.
        if (filter === WHOLE_GROUP_FILTER || filter === companyId) {
          setPosts((prev) => [newPost, ...prev]);
          if (event) reloadSidebar();
        } else {
          handleFilterChange(WHOLE_GROUP_FILTER);
        }
      } catch (err: any) {
        console.error("Error creating post:", err);
        if (!isEmptyUploadError(err)) setError(t("feed_failed_to_create_post"));
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [companyId, filter, handleFilterChange, reloadSidebar, t]
  );

  // The pressed reaction shows at once; the server's counts replace it, and a failure puts the old ones back.
  const handleReact = useCallback(
    async (postId: string, reaction: Reaction) => {
      const current = posts.find((post) => post.id === postId)?.reactions;
      if (!current) return;

      const showReactions = (reactions: ReactionSummary[]) =>
        setPosts((prev) => prev.map((post) => (post.id === postId ? { ...post, reactions } : post)));
      try {
        await toggleReactionOptimistically({
          current,
          reaction,
          show: showReactions,
          send: () => toggleReaction(companyId, postId, reaction),
        });
      } catch (err: any) {
        console.error("Error toggling reaction:", err);
        setError(t("news_reaction_failed"));
      }
    },
    [companyId, posts, t]
  );

  // Handle comment
  const handleComment = useCallback(
    async (postId: string, comment: string, parent?: string): Promise<PostComment> => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      try {
        const newComment = await addComment(companyId, postId, comment, parent);
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  commentsCount: post.commentsCount + 1,
                  comments: [...(post.comments || []), newComment],
                }
              : post
          )
        );
        return newComment;
      } catch (err: any) {
        console.error("Error adding comment:", err);
        throw err;
      }
    },
    [companyId]
  );

  // Handle update comment
  const handleUpdateComment = useCallback(
    async (postId: string, commentId: string, comment: string): Promise<PostComment> => {
      if (!companyId) throw new Error("Company ID is required");

      try {
        const updatedComment = await updateComment(companyId, postId, commentId, comment);
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  comments: (post.comments || []).map((c) =>
                    c.id === commentId ? { ...c, comment: updatedComment.comment } : c
                  ),
                }
              : post
          )
        );
        return updatedComment;
      } catch (err: any) {
        console.error("Error updating comment:", err);
        throw err;
      }
    },
    [companyId]
  );

  // Handle delete comment
  const handleDeleteComment = useCallback(
    async (postId: string, commentId: string) => {
      if (!companyId) return;

      try {
        await deleteComment(companyId, postId, commentId);
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  commentsCount: Math.max(0, post.commentsCount - 1),
                  comments: (post.comments || []).filter(
                    (c) => c.id !== commentId
                  ),
                }
              : post
          )
        );
      } catch (err: any) {
        console.error("Error deleting comment:", err);
        throw err;
      }
    },
    [companyId]
  );

  // Handle update post
  const handleUpdatePost = useCallback(
    async ({ content, details, isImportant, files, removedFileIds, isGroupPost, event }: PostFormSubmission): Promise<void> => {
      if (!companyId || !editingPost) return;

      setIsUpdating(true);
      try {
        // Upload files first if any
        let fileIds: string[] | undefined = undefined;
        
        // Get existing file IDs, excluding removed ones
        const existingFileIds = editingPost.files
          .filter(f => !removedFileIds.includes(f.id))
          .map(f => f.id);
        
        if (files.length > 0) {
          try {
            const uploadResults = await Promise.all(
              files.map((file) => upload(file, companyId))
            );
            const validFileIds = uploadResults
              .map((response) => {
                const fileData = response.data;
                if (typeof fileData === 'string') {
                  return fileData;
                } else if (fileData?.id) {
                  return fileData.id;
                } else if (fileData?.fileId) {
                  return fileData.fileId;
                }
                return null;
              })
              .filter((id): id is string => id !== null);
            
            // Merge with existing file IDs
            fileIds = [...existingFileIds, ...validFileIds];
          } catch (fileError) {
            console.error("Error uploading files:", fileError);
            throw fileError;
          }
        } else {
          // If no new files, use existing file IDs (empty array if all were removed)
          fileIds = existingFileIds;
        }

        const updatedPost = await updatePost({
          companyId,
          postId: editingPost.id,
          content,
          details,
          isImportant,
          fileIds,
          isGroupPost,
          event,
        });
        setPosts((prev) =>
          prev.map((post) =>
            post.id === editingPost.id ? { ...post, ...updatedPost } : post
          )
        );
        if (event) reloadSidebar();
        setEditingPost(null);
      } catch (err: any) {
        console.error("Error updating post:", err);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [companyId, editingPost, reloadSidebar]
  );

  // Handle delete post
  const handleDeletePost = useCallback(
    async (postId: string) => {
      if (!companyId) return;

      const wasEvent = posts.some((post) => post.id === postId && post.event);
      try {
        await deletePost(companyId, postId);
        setPosts((prev) => prev.filter((post) => post.id !== postId));
        if (wasEvent) reloadSidebar();
      } catch (err: any) {
        console.error("Error deleting post:", err);
        throw err;
      }
    },
    [companyId, posts, reloadSidebar]
  );

  // Answer an event from its post or from the sidebar; both lists pick up the new count.
  const handleEventResponse = useCallback(
    async (postId: string, response: EventResponse | null) => {
      try {
        const { event } = await setEventResponse(companyId, postId, response);
        setPosts((prev) =>
          prev.map((post) =>
            post.id === postId && post.event ? { ...post, event: { ...post.event, ...event } } : post
          )
        );
        reloadSidebar();
      } catch (err: any) {
        console.error("Error answering event:", err);
        setError(t("news_event_response_failed"));
      }
    },
    [companyId, reloadSidebar, t]
  );

  // Pin or unpin. The first page is loaded again instead of patching the list: the server alone knows which
  // posts are pinned and where the unpinned ones continue, so no post is skipped when the next page comes.
  // A pinned post is then scrolled to, since it has just moved to the top section.
  const handlePin = useCallback(
    async (postId: string, request: PinRequest) => {
      try {
        await pinPost(companyId, postId, request);
      } catch (err: any) {
        console.error("Error pinning post:", err);
        setError(t("news_pin_failed"));
        return;
      }
      loadPosts(1, false);
      if (request.pinned) setPostIdToScrollTo(postId);
    },
    [companyId, loadPosts, t]
  );

  const handleAcknowledge = useCallback(
    async (postId: string) => {
      try {
        const { pin } = await acknowledgePost(companyId, postId);
        setPosts((prev) => prev.map((post) => (post.id === postId ? { ...post, pin } : post)));
      } catch (err: any) {
        console.error("Error acknowledging post:", err);
        setError(t("news_ack_failed"));
      }
    },
    [companyId, t]
  );

  // A sidebar click only names the post; the two effects below bring it onto the page and scroll to it.
  // Both wait for the first page to finish loading: until then no list is on the page, and the
  // first page, when it arrives, replaces every post that was added meanwhile.
  const isPostToScrollToOnPage = posts.some((post) => post.id === postIdToScrollTo);

  // A post on no loaded page is fetched and put on top. The cleanup runs when the pick changes (or the
  // first page reloads), so the answer of an older fetch, a failure included, never touches the newer pick.
  useEffect(() => {
    if (!postIdToScrollTo || isLoading || isPostToScrollToOnPage) return;

    let isStillWanted = true;
    getPostById(companyId, postIdToScrollTo)
      .then((post) => {
        if (!isStillWanted) return;
        setPosts((prev) => (prev.some((shown) => shown.id === post.id) ? prev : [post, ...prev]));
      })
      .catch((err) => {
        if (!isStillWanted) return;
        console.error("Error loading event post:", err);
        setError(t("feed_failed_to_load_posts"));
        setPostIdToScrollTo(null);
      });
    return () => {
      isStillWanted = false;
    };
  }, [companyId, postIdToScrollTo, isLoading, isPostToScrollToOnPage, t]);

  // The card is on the page: scroll to it, and the sidebar can ask for the same post again.
  useEffect(() => {
    if (!postIdToScrollTo || isLoading || !isPostToScrollToOnPage) return;

    document.getElementById(postElementId(postIdToScrollTo))?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPostIdToScrollTo(null);
  }, [postIdToScrollTo, isLoading, isPostToScrollToOnPage]);

  // Get file URL helper
  const handleGetFileUrl = useCallback(
    async (fileId: string): Promise<string> => {
      if (!companyId) throw new Error("Company ID is required");
      return getFileUrl(companyId, fileId);
    },
    [companyId]
  );

  // Refresh posts
  const handleRefresh = useCallback(() => {
    loadPosts(1, false);
    loadNewsContext();
    reloadSidebar();
  }, [loadPosts, loadNewsContext, reloadSidebar]);

  // Pinned posts form a section above the feed, the latest pin first.
  const pinnedPosts = posts.filter(isPinned).sort((a, b) => b.pin.pinnedAt - a.pin.pinnedAt);
  const unpinnedPosts = posts.filter((post) => !isPinned(post));

  const renderPostCard = (post: Post) => (
    <PostCard
      key={post.id}
      post={post}
      companyId={companyId}
      isMaster={viewer?.isMaster ?? false}
      canOrganize={viewer?.canOrganize ?? false}
      canManagePosts={viewer?.isMember ?? false}
      onReact={handleReact}
      onComment={handleComment}
      onDeleteComment={handleDeleteComment}
      onUpdateComment={handleUpdateComment}
      onDeletePost={handleDeletePost}
      onEditPost={setEditingPost}
      onEventResponse={handleEventResponse}
      onPin={handlePin}
      onAcknowledge={handleAcknowledge}
      getFileUrl={handleGetFileUrl}
    />
  );

  return (
    <div className="bg-gray-50" data-testid="posts-page">
      <div className="mx-auto max-w-2xl p-4 pb-8 lg:max-w-3xl lg:px-6 xl:max-w-6xl 2xl:max-w-7xl">
        <NewsHeader
          today={sidebar?.today}
          absentCount={sidebar?.absentToday?.total ?? 0}
          birthdayCount={sidebar?.birthdays.todayCount ?? 0}
          onRefresh={handleRefresh}
        >
          {group && <FeedFilter companies={group.companies} value={filter} onChange={handleFilterChange} />}
        </NewsHeader>

        {/* Below xl: one column at most 3xl wide, side blocks above the feed (the sidebar sets its own order). */}
        {/* From xl: side blocks beside the feed, the page widens up to 7xl and the feed stays at most 3xl so lines remain readable. */}
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:gap-8">
          <div className="min-w-0 xl:max-w-3xl xl:flex-1">
            {/* Error Banner */}
            {(contextError || error) && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {contextError || error}
                <button
                  onClick={() => {
                    setContextError(null);
                    setError(null);
                  }}
                  className="float-right text-red-500 hover:text-red-700"
                >
                  ×
                </button>
              </div>
            )}

            {/* Post Form - only members post. Editing opens the full form; creating starts as a one-line prompt. */}
            {viewer?.canPost &&
              (editingPost ? (
                <PostForm
                  key={editingPost.id}
                  onSubmit={handleUpdatePost}
                  isSubmitting={isUpdating}
                  initialContent={editingPost.content}
                  initialDetails={editingPost.details}
                  initialIsImportant={editingPost.isImportant}
                  postId={editingPost.id}
                  onCancel={() => setEditingPost(null)}
                  submitButtonText={t("feed_save")}
                  existingFiles={editingPost.files}
                  getFileUrl={handleGetFileUrl}
                  initialIsGroupPost={editingPost.isGroupPost}
                  showGroupPostCheckbox={viewer.canPostToGroup}
                  showGroupEventCheckbox={viewer.canPostToGroup || viewer.canOrganizeGroupEvents}
                  canHaveEvent={viewer.canOrganize}
                  initialEvent={editingPost.event}
                />
              ) : isComposerOpen ? (
                <PostForm
                  key="new-post"
                  onSubmit={handleCreatePost}
                  isSubmitting={isSubmitting}
                  onCancel={() => setIsComposerOpen(false)}
                  showGroupPostCheckbox={viewer.canPostToGroup}
                  showGroupEventCheckbox={viewer.canPostToGroup || viewer.canOrganizeGroupEvents}
                  canHaveEvent={viewer.canOrganize}
                  startAsEvent={composerStartsAsEvent}
                />
              ) : (
                <ComposerPill onOpen={() => openComposer(false)} />
              ))}

            {/* Posts List */}
            {isLoading ? (
              <PostSkeletonList count={3} />
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("feed_no_posts_yet")}
                </h3>
                <p className="text-gray-600">
                  {t("feed_be_first_to_share")}
                </p>
              </div>
            ) : (
              <>
                {pinnedPosts.length > 0 && <section data-testid="pinned-posts">{pinnedPosts.map(renderPostCard)}</section>}
                {unpinnedPosts.map(renderPostCard)}

                {/* Loading More Indicator */}
                {isLoadingMore && (
                  <div className="mt-4">
                    <PostSkeletonList count={2} />
                  </div>
                )}

                {/* Observer Target for Infinite Scroll */}
                <div ref={observerTarget} className="h-4"></div>

                {/* End of Feed Message */}
                {!hasNextPage && posts.length > 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <p>{t("feed_all_caught_up")}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {sidebar && (
            <NewsSidebar
              sidebar={sidebar}
              companyId={companyId}
              canOrganize={viewer?.canOrganize ?? false}
              onOpenEvent={setPostIdToScrollTo}
              onEventResponse={handleEventResponse}
              onCreateEvent={() => openComposer(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

